import { z } from "zod";

const text = z.string().trim().min(1);
const optionalText = z.string().trim().nullish().catch(null);
const categorySchema = z.object({
  id: text,
  title: text,
  description: optionalText,
  time: optionalText,
});
const mentorSchema = z.object({
  id: text,
  name: text,
  title: text,
  categoryIds: z.array(text).min(1),
  pronouns: optionalText,
  profileUrl: z
    .string()
    .url()
    .refine((url) => /^https?:\/\//i.test(url))
    .nullish()
    .catch(null),
  headshotUrl: z
    .string()
    .trim()
    .url()
    .refine((url) => /^https?:\/\//i.test(url))
    .nullish()
    .catch(null),
});

export type MentorCafeCategory = z.infer<typeof categorySchema>;
export type MentorCafeProfile = z.infer<typeof mentorSchema>;
export interface MentorCafeConfig {
  categories: MentorCafeCategory[];
  mentors: MentorCafeProfile[];
}

// One malformed record must not hide the entire lineup or other event metadata.
export const parseMentorCafe = (value: unknown): MentorCafeConfig => {
  const envelope = z
    .object({ categories: z.array(z.unknown()), mentors: z.array(z.unknown()) })
    .safeParse(value);
  if (!envelope.success) return { categories: [], mentors: [] };
  const categories: MentorCafeCategory[] = [];
  const mentors: MentorCafeProfile[] = [];
  envelope.data.categories.forEach((raw) => {
    const result = categorySchema.safeParse(raw);
    if (result.success && !categories.some(({ id }) => id === result.data.id))
      categories.push(result.data);
  });
  envelope.data.mentors.forEach((raw) => {
    const result = mentorSchema.safeParse(raw);
    if (!result.success || mentors.some(({ id }) => id === result.data.id))
      return;
    const categoryIds = Array.from(new Set(result.data.categoryIds)).filter(
      (id) => categories.some((category) => category.id === id)
    );
    if (categoryIds.length) mentors.push({ ...result.data, categoryIds });
  });
  return { categories, mentors };
};
